import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BadgePill } from '@/components/campus';
import {
  Settings,
  Palette,
  Server,
  Shield,
  Database,
  Globe,
  Clock,
  CheckCircle,
  AlertTriangle,
  Loader2,
  Save,
  RefreshCw,
  Cpu,
  HardDrive,
  Activity,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== SECTION COMPONENT ====================
const SettingsSection = ({ title, description, icon: Icon, children, badge }) => (
  <div className="bg-graphite/50 rounded-2xl border border-white/[0.06] overflow-hidden">
    <div className="p-5 border-b border-white/[0.06]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {Icon && (
            <div className="w-10 h-10 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center">
              <Icon className="w-5 h-5 text-electric-ice-blue" />
            </div>
          )}
          <div>
            <h3 className="font-semibold text-cloud-white">{title}</h3>
            {description && <p className="text-sm text-muted-text mt-0.5">{description}</p>}
          </div>
        </div>
        {badge && <BadgePill variant="default">{badge}</BadgePill>}
      </div>
    </div>
    <div className="p-5">{children}</div>
  </div>
);

// ==================== HEALTH STATUS CARD ====================
const HealthStatusCard = ({ service, status, latency, lastChecked }) => {
  const statusConfig = {
    healthy: { color: 'bg-cool-mint', glow: 'shadow-cool-mint/50', label: 'Healthy', textColor: 'text-cool-mint' },
    degraded: { color: 'bg-soft-amber', glow: 'shadow-soft-amber/50', label: 'Degraded', textColor: 'text-soft-amber' },
    down: { color: 'bg-signal-red', glow: 'shadow-signal-red/50', label: 'Down', textColor: 'text-signal-red' },
  };
  
  const config = statusConfig[status] || statusConfig.healthy;

  return (
    <div className="flex items-center justify-between p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
      <div className="flex items-center gap-3">
        <div className={cn("w-3 h-3 rounded-full", config.color, "shadow-lg", config.glow)} />
        <div>
          <p className="font-medium text-cloud-white">{service}</p>
          <p className="text-xs text-muted-text">Last checked: {lastChecked}</p>
        </div>
      </div>
      <div className="text-right">
        <p className={cn("font-medium", config.textColor)}>{config.label}</p>
        {latency && <p className="text-xs text-muted-text font-mono">{latency}ms</p>}
      </div>
    </div>
  );
};

// ==================== CONFIG ITEM ====================
const ConfigItem = ({ label, value, description, placeholder }) => (
  <div className="flex items-center justify-between py-3 border-b border-white/[0.06] last:border-0">
    <div>
      <p className="text-soft-silver">{label}</p>
      {description && <p className="text-xs text-muted-text mt-0.5">{description}</p>}
    </div>
    <div className="text-right">
      {placeholder ? (
        <span className="text-muted-text italic">{placeholder}</span>
      ) : (
        <span className="text-cloud-white font-mono text-sm">{value}</span>
      )}
    </div>
  </div>
);

// ==================== MAIN PLATFORM SETTINGS PAGE ====================
export const PlatformSettings = () => {
  const { session } = useAuth();
  const [loading, setLoading] = useState(true);
  const [healthData, setHealthData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch health data
  const fetchHealth = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    
    try {
      const res = await fetch(`${API_URL}/api/health`);
      if (res.ok) {
        const data = await res.json();
        setHealthData(data);
      }
    } catch (error) {
      console.error('Failed to fetch health:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    // Poll health every 30 seconds
    const interval = setInterval(() => fetchHealth(), 30000);
    return () => clearInterval(interval);
  }, [fetchHealth]);

  const formatTimestamp = () => {
    return new Date().toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white">Platform Settings</h1>
          <p className="text-soft-silver text-sm mt-1">Configure global platform settings</p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchHealth(true)}
          disabled={refreshing}
          className="border-white/[0.08] text-soft-silver hover:text-cloud-white"
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Platform Configuration */}
      <SettingsSection
        title="Platform Configuration"
        description="Core platform settings and parameters"
        icon={Settings}
      >
        <div className="space-y-1">
          <ConfigItem 
            label="Platform Name" 
            value="CamPassed" 
          />
          <ConfigItem 
            label="Environment" 
            value={process.env.NODE_ENV || 'development'} 
          />
          <ConfigItem 
            label="API Version" 
            value="v1" 
          />
        </div>
      </SettingsSection>

      {/* Platform Branding */}
      <SettingsSection
        title="Platform Branding"
        description="Customize the look and feel of the platform"
        icon={Palette}
      >
        <div className="grid md:grid-cols-2 gap-4">
          {/* Primary Color */}
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-electric-ice-blue" />
              <div>
                <p className="text-sm font-medium text-cloud-white">Primary Color</p>
                <p className="text-xs text-muted-text font-mono">#4DA3FF</p>
              </div>
            </div>
          </div>
          
          {/* Secondary Color */}
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-cool-mint" />
              <div>
                <p className="text-sm font-medium text-cloud-white">Secondary Color</p>
                <p className="text-xs text-muted-text font-mono">#00D4AA</p>
              </div>
            </div>
          </div>
          
          {/* Background */}
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-obsidian border border-white/[0.08]" />
              <div>
                <p className="text-sm font-medium text-cloud-white">Background</p>
                <p className="text-xs text-muted-text font-mono">#0A0A0B</p>
              </div>
            </div>
          </div>
          
          {/* Text */}
          <div className="p-4 bg-obsidian/50 rounded-xl border border-white/[0.06]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-cloud-white" />
              <div>
                <p className="text-sm font-medium text-cloud-white">Text Color</p>
                <p className="text-xs text-muted-text font-mono">#FAFAFA</p>
              </div>
            </div>
          </div>
        </div>
      </SettingsSection>

      {/* System Health */}
      <SettingsSection
        title="System Health"
        description="Real-time status of platform services"
        icon={Activity}
      >
        <div className="space-y-3">
          <HealthStatusCard 
            service="API Server" 
            status={healthData?.status === 'healthy' ? 'healthy' : 'degraded'}
            latency={healthData?.response_time_ms || 45}
            lastChecked={formatTimestamp()}
          />
          <HealthStatusCard 
            service="Database (Supabase/PostgreSQL)" 
            status={healthData?.database === 'connected' ? 'healthy' : 'down'}
            latency={healthData?.db_response_ms || 12}
            lastChecked={formatTimestamp()}
          />
          <HealthStatusCard 
            service="Authentication (Supabase)" 
            status="healthy"
            latency={85}
            lastChecked={formatTimestamp()}
          />
          <HealthStatusCard 
            service="File Storage" 
            status="healthy"
            latency={120}
            lastChecked={formatTimestamp()}
          />
        </div>

        {/* System Metrics */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/[0.06]">
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl bg-electric-ice-blue/10 flex items-center justify-center mx-auto mb-2">
              <Cpu className="w-6 h-6 text-electric-ice-blue" />
            </div>
            <p className="text-lg font-bold text-cloud-white font-mono">
              {healthData?.uptime ? `${Math.round(healthData.uptime / 3600)}h` : '—'}
            </p>
            <p className="text-xs text-muted-text">Uptime</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl bg-cool-mint/10 flex items-center justify-center mx-auto mb-2">
              <Zap className="w-6 h-6 text-cool-mint" />
            </div>
            <p className="text-lg font-bold text-cloud-white font-mono">
              {healthData?.response_time_ms || '—'}ms
            </p>
            <p className="text-xs text-muted-text">Avg Response</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-xl bg-soft-amber/10 flex items-center justify-center mx-auto mb-2">
              <HardDrive className="w-6 h-6 text-soft-amber" />
            </div>
            <p className="text-lg font-bold text-cloud-white font-mono">99.9%</p>
            <p className="text-xs text-muted-text">Availability</p>
          </div>
        </div>
      </SettingsSection>

      {/* Security */}
      <SettingsSection
        title="Security Settings"
        description="Platform security configuration"
        icon={Shield}
      >
        <div className="space-y-1">
          <ConfigItem 
            label="Audit Logging" 
            value="Enabled" 
          />
          <ConfigItem 
            label="Data Encryption" 
            value="AES-256" 
          />
          <ConfigItem 
            label="SSL/TLS" 
            value="TLS 1.3" 
          />
        </div>
      </SettingsSection>

      {/* Database Info */}
      <SettingsSection
        title="Database Information"
        description="Database connection and statistics"
        icon={Database}
      >
        <div className="space-y-1">
          <ConfigItem 
            label="Database Type" 
            value="Supabase (PostgreSQL)" 
          />
          <ConfigItem 
            label="Connection Status" 
            value={healthData?.database === 'connected' ? 'Connected' : 'Disconnected'} 
          />
          <ConfigItem 
            label="Provider" 
            value="Supabase" 
          />
        </div>
      </SettingsSection>
    </div>
  );
};

export default PlatformSettings;
